import type { FormAnswers, FormAnswerValue } from '../types/forms';
import type { AnswerInputPayload, SubmissionDetail, SubmissionQuestion } from '../types/formSubmissions';

// Fase 23B: conversion PURA entre el estado local del runner y el payload/detalle del backend, y a la
// forma que consume el evaluador de visibilidad (23A). Separado del componente para poder probarlo.

// Estado local: questionId -> valor segun tipo (boolean | string | number | string[]).
export type RunnerAnswers = Record<string, FormAnswerValue>;

export function answersFromDetail(detail: Pick<SubmissionDetail, 'questions' | 'answers'>): RunnerAnswers {
  const byId = new Map(detail.questions.map((q) => [q.id, q]));
  const state: RunnerAnswers = {};
  for (const a of detail.answers) {
    const q = byId.get(a.questionId);
    if (!q) continue;
    switch (q.type) {
      case 'YES_NO': if (a.valueBool !== null) state[a.questionId] = a.valueBool; break;
      case 'NUMBER': if (a.valueNumber !== null) state[a.questionId] = a.valueNumber; break;
      case 'DATE': if (a.valueDate !== null) state[a.questionId] = a.valueDate; break;
      case 'TIME': if (a.valueTime !== null) state[a.questionId] = a.valueTime; break;
      case 'SINGLE_SELECT': if (a.optionIds[0]) state[a.questionId] = a.optionIds[0]; break;
      case 'MULTI_SELECT': if (a.optionIds.length) state[a.questionId] = a.optionIds; break;
      default: if (a.valueText !== null) state[a.questionId] = a.valueText; break;
    }
  }
  return state;
}

const hasValue = (v: FormAnswerValue): boolean => {
  if (v === null || v === undefined) return false;
  if (typeof v === 'string') return v.trim() !== '';
  if (Array.isArray(v)) return v.length > 0;
  return true; // boolean/number
};

// Estado local -> payload del backend (solo preguntas con un valor). No filtra por visibilidad: el
// backend descarta las ocultas al enviar (autoritativo); el autosave puede guardar datos condicionales.
export function answersToPayload(questions: SubmissionQuestion[], state: RunnerAnswers): AnswerInputPayload[] {
  const out: AnswerInputPayload[] = [];
  for (const q of questions) {
    const v = state[q.id];
    if (!hasValue(v)) continue;
    switch (q.type) {
      case 'YES_NO': out.push({ questionId: q.id, valueBool: v as boolean }); break;
      case 'NUMBER': out.push({ questionId: q.id, valueNumber: typeof v === 'number' ? v : Number(v) }); break;
      case 'DATE': out.push({ questionId: q.id, valueDate: String(v) }); break;
      case 'TIME': out.push({ questionId: q.id, valueTime: String(v) }); break;
      case 'SINGLE_SELECT': out.push({ questionId: q.id, optionIds: [String(v)] }); break;
      case 'MULTI_SELECT': out.push({ questionId: q.id, optionIds: (v as string[]) }); break;
      default: out.push({ questionId: q.id, valueText: String(v) }); break;
    }
  }
  return out;
}

// Estado local -> forma que consume evaluateFormVisibility (23A). Solo valores presentes.
export function answersToVisibilityInput(state: RunnerAnswers): FormAnswers {
  const out: FormAnswers = {};
  for (const [k, v] of Object.entries(state)) if (hasValue(v)) out[k] = v;
  return out;
}
